function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import _extends from "@babel/runtime/helpers/esm/extends";
import { warnOnce } from '@mui/x-internals/warning';
import { gridRowTreeSelector } from "../rows/gridRowsSelector.js";
export var sanitizeSortModel = function(model, disableMultipleColumnsSorting) {
    if (disableMultipleColumnsSorting && model.length > 1) {
        if (process.env.NODE_ENV !== 'production') {
            warnOnce([
                'MUI X: The `sortModel` can only contain a single item when the `disableMultipleColumnsSorting` prop is set to `true`.',
                'If you are using the community version of the Data Grid, this prop is always `true`.'
            ], 'error');
        }
        return [
            model[0]
        ];
    }
    return model;
};
export var mergeStateWithSortModel = function(sortModel, disableMultipleColumnsSorting) {
    return function(state) {
        return _extends({}, state, {
            sorting: _extends({}, state.sorting, {
                sortModel: sanitizeSortModel(sortModel, disableMultipleColumnsSorting)
            })
        });
    };
};
var isDesc = function(direction) {
    return direction === 'desc';
};
/**
 * Transform an item of the sorting model into a method comparing two rows.
 * @param {GridSortItem} sortItem The sort item we want to apply.
 * @param {RefObject<GridApiCommunity>} apiRef The API of the grid.
 * @returns {GridParsedSortItem | null} The parsed sort item. Returns `null` is the sort item is not valid.
 */ var parseSortItem = function(sortItem, apiRef) {
    var column = apiRef.current.getColumn(sortItem.field);
    if (!column || sortItem.sort === null) {
        return null;
    }
    var comparator;
    if (column.getSortComparator) {
        comparator = column.getSortComparator(sortItem.sort);
    } else {
        var _column;
        comparator = isDesc(sortItem.sort) ? function() {
            for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                args[_key] = arguments[_key];
            }
            return -1 * (_column = column).sortComparator.apply(_column, _to_consumable_array(args));
        } : column.sortComparator;
    }
    if (!comparator) {
        return null;
    }
    var getSortCellParams = function(id) {
        return {
            id: id,
            field: column.field,
            rowNode: gridRowTreeSelector(apiRef)[id],
            value: apiRef.current.getCellValue(id, column.field),
            api: apiRef.current
        };
    };
    return {
        getSortCellParams: getSortCellParams,
        comparator: comparator
    };
};
/**
 * Compare two rows according to a list of valid sort items.
 * The `row1Params` and `row2Params` must have the same length as `parsedSortItems`,
 * and each of their index must contain the `GridSortCellParams` of the sort item with the same index.
 * @param {GridParsedSortItem[]} parsedSortItems All the sort items with which we want to compare the rows.
 * @param {GridRowAggregatedSortingParams} row1 The node and params of the 1st row for each sort item.
 * @param {GridRowAggregatedSortingParams} row2 The node and params of the 2nd row for each sort item.
 */ var compareRows = function(parsedSortItems, row1, row2) {
    return parsedSortItems.reduce(function(res, item, index) {
        if (res !== 0) {
            // return the results of the first comparator which distinguish the two rows
            return res;
        }
        var sortCellParams1 = row1.params[index];
        var sortCellParams2 = row2.params[index];
        res = item.comparator(sortCellParams1.value, sortCellParams2.value, sortCellParams1, sortCellParams2);
        return res;
    }, 0);
};
/**
 * Generates a method to easily sort a list of rows according to the current sort model.
 * @param {GridSortModel} sortModel The model with which we want to sort the rows.
 * @param {RefObject<GridApiCommunity>} apiRef The API of the grid.
 * @returns {GridSortingModelApplier | null} A method that generates a list of sorted row ids from a list of rows according to the current sort model. If `null`, we consider that the rows should remain in the order there were provided.
 */ export var buildAggregatedSortingApplier = function(sortModel, apiRef) {
    var comparatorList = sortModel.map(function(item) {
        return parseSortItem(item, apiRef);
    }).filter(function(comparator) {
        return !!comparator;
    });
    if (comparatorList.length === 0) {
        return null;
    }
    return function(rowList) {
        return rowList.map(function(node) {
            return {
                node: node,
                params: comparatorList.map(function(el) {
                    return el.getSortCellParams(node.id);
                })
            };
        }).sort(function(a, b) {
            return compareRows(comparatorList, a, b);
        }).map(function(row) {
            return row.node.id;
        });
    };
};
export var getNextGridSortDirection = function(sortingOrder, current) {
    var currentIdx = sortingOrder.indexOf(current);
    if (!current || currentIdx === -1 || currentIdx + 1 === sortingOrder.length) {
        return sortingOrder[0];
    }
    return sortingOrder[currentIdx + 1];
};
var gridNillComparator = function(v1, v2) {
    if (v1 == null && v2 != null) {
        return -1;
    }
    if (v2 == null && v1 != null) {
        return 1;
    }
    if (v1 == null && v2 == null) {
        return 0;
    }
    return null;
};
var collator = new Intl.Collator();
export var gridStringOrNumberComparator = function(value1, value2) {
    var nillResult = gridNillComparator(value1, value2);
    if (nillResult !== null) {
        return nillResult;
    }
    if (typeof value1 === 'string') {
        return collator.compare(value1.toString(), value2.toString());
    }
    return value1 - value2;
};
export var gridNumberComparator = function(value1, value2) {
    var nillResult = gridNillComparator(value1, value2);
    if (nillResult !== null) {
        return nillResult;
    }
    return Number(value1) - Number(value2);
};
export var gridDateComparator = function(value1, value2) {
    var nillResult = gridNillComparator(value1, value2);
    if (nillResult !== null) {
        return nillResult;
    }
    if (value1 > value2) {
        return 1;
    }
    if (value1 < value2) {
        return -1;
    }
    return 0;
};
